import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X, Loader2 } from "lucide-react";
import { streamReaderAsk } from "@/lib/api";

/**
 * ReaderCompanion — floating "Ask Felicia" chat drawer.
 * Streams Claude Haiku responses grounded in the current chapter.
 * Session ID persists per browser tab so follow-up messages have memory.
 */
export const ReaderCompanion = ({ chapterId, chapterLabel }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]); // {role: 'user'|'assistant', text}
  const [streaming, setStreaming] = useState(false);
  const sessionIdRef = useRef(`reader-${Math.random().toString(36).slice(2, 10)}`);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { setMessages([]); }, [chapterId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (e) => {
    e?.preventDefault?.();
    const q = input.trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setStreaming(true);
    abortRef.current?.();
    abortRef.current = streamReaderAsk({
      chapterId,
      message: q,
      sessionId: sessionIdRef.current,
      onDelta: (delta) => {
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") next[next.length - 1] = { ...last, text: last.text + delta };
          return next;
        });
      },
      onDone: () => setStreaming(false),
      onError: () => {
        setStreaming(false);
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant" && !last.text) {
            next[next.length - 1] = { ...last, text: "Sorry — I couldn't answer that. Please try again." };
          }
          return next;
        });
      },
    });
  };

  return (
    <>
      {/* Trigger — floating button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="reader-companion-open"
        aria-label="Ask Felicia"
        className="fixed bottom-6 right-6 z-30 bg-gold text-ink px-5 py-3 flex items-center gap-2 shadow-lg hover:bg-cream transition-colors"
      >
        <Sparkles className="w-4 h-4" />
        <span className="font-body text-sm font-medium">Ask Felicia</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 pointer-events-none" data-testid="reader-companion-panel">
          <div
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/40 pointer-events-auto"
          />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-cream border-l border-line flex flex-col pointer-events-auto"
            style={{ background: "var(--reader-bg, #FAFAFA)", color: "var(--reader-ink, #0A0A0A)" }}
          >
            <div className="p-5 border-b border-line flex items-center justify-between">
              <div>
                <p className="eyebrow text-emerald mb-1 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> Reader Companion
                </p>
                <p className="font-display text-lg leading-tight">Ask about {chapterLabel || "this chapter"}</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="reader-companion-close"
                className="p-2 text-ink/50 hover:text-ink"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-4" data-testid="reader-companion-transcript">
              {messages.length === 0 && (
                <div className="text-center pt-8">
                  <p className="font-editorial italic text-lg text-emerald mb-6">
                    A companion for slow reading.
                  </p>
                  <p className="font-body text-xs text-ink/60 mb-4">Try asking:</p>
                  <div className="space-y-2">
                    {[
                      "What's the core teaching of this chapter?",
                      "Give me one small practice for this week.",
                      "How does this chapter connect to the Five Pillars?",
                    ].map((s) => (
                      <button
                        key={s}
                        onClick={() => { setInput(s); }}
                        data-testid={`companion-suggest-${s.slice(0, 20).replace(/\s/g, "-")}`}
                        className="w-full text-left px-4 py-2.5 border border-line hover:border-emerald hover:text-emerald text-sm font-body transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "text-right" : ""} data-testid={`companion-msg-${m.role}-${i}`}>
                  <div
                    className={`inline-block max-w-[92%] px-4 py-3 font-body text-sm ${
                      m.role === "user"
                        ? "bg-emerald text-cream"
                        : "bg-surface border border-line"
                    }`}
                    style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}
                  >
                    {m.text || (streaming && i === messages.length - 1 ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "")}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={send} className="p-4 border-t border-line" data-testid="reader-companion-form">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="What is stirring in you?"
                  disabled={streaming}
                  data-testid="reader-companion-input"
                  className="flex-1 bg-surface border border-line focus:border-emerald outline-none px-3 py-2.5 font-body text-sm"
                  style={{ background: "var(--reader-surface, #F2F2EB)" }}
                />
                <button
                  type="submit"
                  disabled={streaming || !input.trim()}
                  data-testid="reader-companion-send"
                  className="bg-emerald text-cream px-4 disabled:opacity-40"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-ink/40 font-body mt-2 text-center">
                Powered by Claude · answers grounded in {chapterLabel || "this chapter"}
              </p>
            </form>
          </aside>
        </div>
      )}
    </>
  );
};

export default ReaderCompanion;
